import React, { useState } from "react";
import styled from "styled-components";
import Icon from "./Icon/Icon";
import SectionHeading from "./SectionHeading";

export interface CollapsibleSectionProps {
	/** The section's name. §2 SectionHeading type — 14px bold, secondary. */
	title: React.ReactNode;
	defaultOpen?: boolean;
	/**
	 * `false` pins the section open: same header, same spacing, but no chevron
	 * and nothing to click. For a section that must always show (the plan-mode
	 * tools) yet sits among collapsible ones and should look like their sibling.
	 */
	collapsible?: boolean;
	/** Applied to the section root, for the layout the call site owns. */
	className?: string;
	children: React.ReactNode;
}

/**
 * A titled block that folds away, and the one collapse treatment in the app.
 *
 * Two places grew their own before this existed: the 3D detail panel folded its
 * object/material/light sections behind a ▲/▼ text glyph and an inline
 * `fontWeight: 500`, while the plan-mode edit panel folded its whole body behind
 * an hds chevron. Same gesture, two different-looking headers — so it lives here
 * now and both call it.
 *
 * The header is the control: the whole bar is clickable, as a real `<button>` so
 * it is reachable by keyboard and announces its state, but with none of a
 * button's chrome. The 12px it keeps to its content is SectionHeading's own gap
 * (§7, "12px down to the first control"), which is why the heading's margin is
 * dropped here — the section owns that distance in both states.
 */
const CollapsibleSection = ({
	title,
	defaultOpen = true,
	collapsible = true,
	className,
	children,
}: CollapsibleSectionProps) => {
	const [open, setOpen] = useState(defaultOpen);

	if (!collapsible) {
		return (
			<Section className={className}>
				<StaticHeader>
					<Title>{title}</Title>
				</StaticHeader>
				<Content>{children}</Content>
			</Section>
		);
	}

	return (
		<Section className={className}>
			<Header
				type="button"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			>
				<Title>{title}</Title>
				{/* Carets (chevrons), never arrows with a shaft — DESIGN_SYSTEM.md §6. */}
				<Icon
					name={open ? "ArrowTop2Icon" : "ArrowBottom2Icon"}
					set="hds"
					size="20px"
				/>
			</Header>
			{open && <Content>{children}</Content>}
		</Section>
	);
};

const Section = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const Header = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-2);
	width: 100%;
	margin: 0;
	/* 8 all round: at 4px vertical the hover ground hugged the title and the
	   chevron like a highlight rather than framing a control. */
	padding: var(--space-2);
	border: none;
	border-radius: var(--radius-sm);
	background: transparent;
	color: var(--color-text-secondary);
	text-align: left;
	cursor: pointer;
	transition: background var(--transition-base);

	&:hover {
		background: var(--color-hover-ground);
	}
`;

/**
 * The pinned-open header: the button's box and inset, so the title sits exactly
 * where a collapsible sibling's does, without its cursor or hover ground. The
 * chevron's 20px is kept as min-height so both headers are equally tall.
 */
const StaticHeader = styled.div`
	display: flex;
	align-items: center;
	min-height: calc(20px + 2 * var(--space-2));
	padding: var(--space-2);
	color: var(--color-text-secondary);
`;

/**
 * SectionHeading carries a 12px bottom margin so a heading cannot forget the gap
 * to its first control. Here the section owns that gap (see `Content`), and the
 * margin would push the chevron off centre — so the heading keeps only its type.
 */
const Title = styled(SectionHeading)`
	margin: 0;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/**
 * 4, not §7's 12, and it still reads as 12: the gap starts at the title, not at
 * the edge of the header's hover ground, and the header keeps 8px of its own
 * below the text. Setting the margin to the full 12 spends that 8 twice and puts
 * 20px between a section's name and its first row — loose in a 300px rail,
 * conspicuous in the 3D detail panel where several sections stack.
 */
const Content = styled.div`
	margin-top: var(--space-1);
	min-width: 0;
`;

export default CollapsibleSection;
